import type {
  IAssignment,
  IAssignmentQuery,
} from '@shiftsmartinc/shiftsmart-types';

import { action, observable, computed, IObservableArray } from 'mobx';
import _ from 'lodash';
import moment from 'moment';
import { dispatch } from 'rfx-core';

import { getChildLogger } from '#/shared/utils/client.logger';

const log = getChildLogger('ui.PartnerDetails');

const shiftSort = (a, b) => {
  if (!a.start) return 1;
  if (!b.start) return -1;
  return new Date(a.start).getTime() - new Date(b.start).getTime();
};

export default class PartnerDetails {
  statusMap = {
    'All Shifts': [
      'Assigned',
      'Accepted',
      'Approved',
      'Completed',
      'PendingApproval',
      'Sent',
      'NoShow',
    ],
    Assigned: ['Assigned', 'Accepted', 'Approved'],
    Available: ['PendingApproval', 'Sent'],
    Completed: ['Completed'],
  };

  @observable dataLoading = {
    Bonuses: false,
    Certificates: false,
    Notes: false,
    Notifications: false,
    Payments: false,
    Pools: false,
    Shifts: false,
    Statistics: false,
    Summary: false,
    'Work And Education': false,
  };

  @action.bound setDataLoading(tabName, loading) {
    this.dataLoading[tabName] = loading;
  }

  @observable dataLoaded = {
    Bonuses: false,
    Certificates: false,
    Notes: false,
    Notifications: false,
    Payments: false,
    Pools: false,
    Shifts: false,
    Statistics: false,
    Summary: false,
    'Work And Education': false,
  };

  @action.bound setDataLoaded(tabName) {
    this.dataLoaded[tabName] = true;
  }

  @observable duration = '1 Week';

  @action.bound setDuration(duration) {
    this.duration = duration;
  }

  @observable
  isDateCalendarOpen = false;

  @action
  toggleCalendarPopup(open = !this.isDateCalendarOpen) {
    this.isDateCalendarOpen = open;
  }

  @action
  setup() {
    this.setActiveTab(_.first(this.menuTabs).key);
  }

  @action
  clear() {
    dispatch('partners.clearSelected');
    dispatch('userStats.clearSelected');
  }

  @observable activeTab;

  @computed
  get menuTabs() {
    const company = dispatch('auth.getCompany');
    const showBonusesFeature = _.get(
      company,
      'settings.featureFlags.enableBonusVisibilityInEmployerPortal',
    );
    const showShopTab = _.get(company, 'modules.retail');

    return _.compact([
      {
        index: 0,
        key: 'summary',
        title: 'Summary',
      },
      dispatch('auth.can', 'read', 'userstat') && {
        index: 1,
        key: 'statistics',
        title: 'Statistics',
      },
      dispatch('auth.can', 'read', 'worker', 'pools') && {
        index: 2,
        key: 'pools',
        title: 'Pools',
      },
      dispatch('auth.can', 'create', 'assignment') && {
        index: 3,
        key: 'shifts',
        title: 'Shifts',
      },
      showShopTab && {
        index: 4,
        key: 'shops',
        title: 'Shops',
      },
      dispatch('auth.can', 'read', 'worker', 'experience') && {
        index: 6,
        key: 'work and education',
        title: 'Work And Education',
      },
      dispatch('auth.can', 'read', 'worker', 'certs') && {
        index: 7,
        key: 'certificates',
        title: 'Certificates',
      },
      showBonusesFeature && {
        index: 8,
        key: 'bonuses',
        title: 'Bonuses',
      },
    ]);
  }

  @action
  setActiveTab(tab) {
    this.activeTab = tab;

    if (!this.dataLoaded[tab]) {
      this.setDataLoading(tab, true);
    }
  }

  @observable shiftMode: 'calendar' | 'list' = 'calendar';

  @action.bound updateShiftMode(mode) {
    this.shiftMode = mode;
  }

  @observable shiftStatusFilter = 'All Shifts';

  @action.bound setShiftStatusFilter(status) {
    this.shiftStatusFilter = status;
  }

  @observable shiftStart = moment().startOf('week');

  @action.bound updateShiftStart(date) {
    this.shiftStart = date;
  }

  @observable shiftEnd = moment().endOf('week');

  @action.bound updateShiftEnd(date) {
    this.shiftEnd = date;
  }

  @action.bound
  async fetchUserShifts({ user, company }) {
    const query: IAssignmentQuery = {
      company: company.uuid,
      dataType: 'shift',
      start: {
        $gte: this.shiftStart.toDate(),
        $lte: this.shiftEnd.toDate(),
      },
      user: user.uuid,
    };

    if (this.statusMap[this.shiftStatusFilter]) {
      // Hack for handling invites
      query.status = {
        $in: this.statusMap[this.shiftStatusFilter],
      };
    }

    try {
      const scheduleQuery = {
        company: company.uuid,
        user: user.uuid,
        week: this.shiftStart.week(),
      };

      const schedule = await dispatch('userSchedule.find', scheduleQuery);

      log.debug('Loaded Partner Schedule', { schedule, scheduleQuery });
    } catch (error) {
      log.error('Failed to load user schedule', error);
    }
    try {
      return await dispatch('assignments.find', query, {
        clear: true,
      });
    } catch (error) {
      dispatch('ui.snackBar.error', 'Unable to load Shifts for Partner');
    }

    return null;
  }

  // #region Shift Conflicts (with other companies)
  conflictShifts: IObservableArray<IAssignment> = observable([]);

  @action.bound setConflictShiftsList(shifts) {
    this.conflictShifts.replace(shifts);
    return Promise.resolve(this.conflictShifts);
  }

  async fetchConflictShifts({ user, company }) {
    const query: IAssignmentQuery = {
      company: {
        $ne: company.uuid,
      },
      dataType: 'shift',
      start: {
        $gte: this.shiftStart.toDate(),
        $lte: this.shiftEnd.toDate(),
      },
      status: {
        $in: this.statusMap[this.shiftStatusFilter],
      },
      user: user.uuid,
    };

    try {
      const { data: assignmentsList } = await dispatch(
        'assignments.runQuery',
        query,
      );
      return this.setConflictShiftsList(assignmentsList || []);
    } catch (error) {
      return Promise.reject(error);
    }
  }
  // #endregion Shift Conflicts (other companies)
}
